import { useEffect, useRef } from "react";
import { useUser, useAuth } from "@clerk/clerk-react";
import { useNavigate, useLocation } from "react-router-dom";
import { syncUser, registerTokenGetter, UserCreate } from "../utils/api";
import { DESIRED_HANDLE_KEY, sanitizeHandle } from "./HeroHandlePicker";

const PUBLIC_PATHS = ["/", "/how-it-works", "/contact", "/pricing", "/terms", "/privacy"];

// Optional Clerk JWT template (D-002 OQ-1). When the human creates a template
// carrying the `email` + `role` claims the server expects and sets this env
// var, apiFetch will send that template's token instead of the default session
// token — no code change needed. Unset (Phase 1) → default session token.
const CLERK_JWT_TEMPLATE = process.env.REACT_APP_CLERK_JWT_TEMPLATE || undefined;

const AuthSync = () => {
  const { isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const wasSignedIn = useRef(false);

  // D-002: bridge Clerk's getToken() into the module-level apiFetch choke point
  // so every api.ts wrapper attaches the bearer token. Registered on mount
  // (before the sync effect below) so the first authenticated call has it.
  useEffect(() => {
    registerTokenGetter(() =>
      CLERK_JWT_TEMPLATE ? getToken({ template: CLERK_JWT_TEMPLATE }) : getToken()
    );
    return () => registerTokenGetter(null);
  }, [getToken]);

  useEffect(() => {
    if (!isSignedIn || !user) {
      wasSignedIn.current = false;
      return;
    }

    const email = user.primaryEmailAddress?.emailAddress;
    if (!email) return;

    // If the visitor picked a handle on the Landing hero before signing up,
    // promote it to Clerk metadata once so MyCard can use it as the default
    // username for their first card. Clear from localStorage so we don't keep
    // overwriting whatever they later choose in the dashboard.
    try {
      const desired = window.localStorage.getItem(DESIRED_HANDLE_KEY);
      if (desired) {
        const sanitized = sanitizeHandle(desired);
        const meta = (user.unsafeMetadata || {}) as Record<string, unknown>;
        if (sanitized.length >= 2 && !meta.desiredHandle) {
          user
            .update({ unsafeMetadata: { ...meta, desiredHandle: sanitized } })
            .catch(() => {});
        }
        window.localStorage.removeItem(DESIRED_HANDLE_KEY);
      }
    } catch {}

    // Sync user to backend
    const userData: UserCreate = {
      username: email.split("@")[0],
      email,
      firstName: user.firstName || undefined,
      lastName: user.lastName || undefined,
      phoneNumber: user.primaryPhoneNumber?.phoneNumber || undefined,
    };

    syncUser(userData).catch(() => {});

    // Redirect to dashboard if user just signed in and is on a public page
    if (!wasSignedIn.current) {
      wasSignedIn.current = true;
      const isPublicPage = PUBLIC_PATHS.includes(location.pathname) ||
        location.pathname.startsWith("/for/");
      if (isPublicPage) {
        navigate("/my-card");
      }
    }
  }, [isSignedIn, user, navigate, location.pathname]);

  return null;
};

export default AuthSync;